import React, {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link, useSearchParams} from 'react-router-dom';
import {DetailSection} from '@/components/football';
import {useWorkspace} from '@/context/WorkspaceContext';
import {PlayerReport, PlayerReportType, playersV2API} from '@/features/players/api';
import {parsePlayerReportsURLState, serializePlayerReportsURLState} from '@/features/players/playerUrlState';
import {playerKeys} from '@/features/players/queries';
import {PlayerReportFilter} from '@/features/players/types';

interface PlayerReportsSectionProps {
  playerId: string;
  readableReportTypes: PlayerReportType[];
}

const labels: Record<PlayerReportType, string> = {
  performance: 'Performance',
  scouting: 'Scouting',
  medical: 'Medical',
  training: 'Training',
};

const value = (input: string | number | boolean | null): string => input === null || input === '' ? 'Not recorded' : typeof input === 'boolean' ? (input ? 'Yes' : 'No') : String(input);
const date = (input: string | null): string => input ? new Date(input).toLocaleDateString('en-GB') : 'Date not recorded';

const Stat: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => <div className="min-w-0"><dt className="break-words text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt><dd className="break-words text-sm text-gray-900 dark:text-gray-100">{children}</dd></div>;

const ReportCard: React.FC<{report: PlayerReport}> = ({report}) => {
  let details: React.ReactNode;
  if (report.report_type === 'performance') {
    details = <><Stat label="Rating">{value(report.rating)}</Stat><Stat label="Minutes">{report.minutes_played === null ? 'Not recorded' : `${report.minutes_played} minutes`}</Stat><Stat label="Goals">{report.goals === null ? 'Not recorded' : `${report.goals} goals`}</Stat><Stat label="Assists">{value(report.assists)}</Stat><Stat label="Distance">{value(report.distance_covered)}</Stat><Stat label="Kind">{value(report.performance_kind)}</Stat></>;
  } else if (report.report_type === 'scouting') {
    details = <><Stat label="Shooting">{value(report.shooting)}</Stat><Stat label="Passing">{value(report.passing)}</Stat><Stat label="Pace">{value(report.pace)}</Stat><Stat label="Dribbling">{value(report.dribbling)}</Stat><Stat label="Defending">{value(report.defending)}</Stat><Stat label="Physical">{value(report.physical)}</Stat></>;
  } else if (report.report_type === 'medical') {
    details = <><Stat label="Injury">{value(report.injury_type)}</Stat><Stat label="Status">{value(report.status)}</Stat><Stat label="Severity">{value(report.severity)}</Stat><Stat label="Notes">{value(report.notes)}</Stat></>;
  } else {
    details = <><Stat label="Attended">{value(report.attended)}</Stat><Stat label="On time">{value(report.turned_up_on_time)}</Stat><Stat label="Fitness">{value(report.fitness_score)}</Stat><Stat label="Endurance">{value(report.endurance_score)}</Stat><Stat label="Intensity">{value(report.intensity_score)}</Stat><Stat label="Attitude">{value(report.attitude_score)}</Stat></>;
  }

  const typeLabel = labels[report.report_type];
  const title = report.summary ?? `${typeLabel} report`;
  return (
    <article className="min-w-0 break-words rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <span className="inline-flex rounded-full bg-club-blue-50 px-2 py-1 text-xs font-semibold text-club-blue-700 dark:bg-club-blue-950 dark:text-club-blue-200">{typeLabel}</span>
          <h2 className="mt-2 break-words font-semibold text-gray-900 dark:text-gray-100"><Link aria-label={`View ${typeLabel} report: ${title}`} className="rounded-sm text-club-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-club-blue-300" to={`/reports/${report.report_type}/${report.id}`}>{title}</Link></h2>
        </div>
        <time className="shrink-0 text-sm text-gray-500 dark:text-gray-400" dateTime={report.report_date ?? undefined}>{date(report.report_date)}</time>
      </div>
      <dl className="mt-4 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">{details}</dl>
    </article>
  );
};

const ReportsPager: React.FC<{page: number; hasNext: boolean; onPage: (page: number) => void}> = ({page, hasNext, onPage}) => (
  <nav aria-label="Reports pagination" className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
    <button type="button" className="min-h-11 rounded-md px-3 font-semibold text-club-blue-700 disabled:opacity-50 dark:text-club-blue-300" aria-label="Previous reports page" disabled={page === 1} onClick={() => onPage(page - 1)}>Previous</button>
    <span className="break-words text-sm text-gray-600 dark:text-gray-300" aria-live="polite">Page {page}</span>
    <button type="button" className="min-h-11 rounded-md px-3 font-semibold text-club-blue-700 disabled:opacity-50 dark:text-club-blue-300" aria-label="Next reports page" disabled={!hasNext} onClick={() => onPage(page + 1)}>Next</button>
  </nav>
);

const PlayerReportsSection: React.FC<PlayerReportsSectionProps> = ({playerId, readableReportTypes}) => {
  const [params, setParams] = useSearchParams();
  const workspaceId = useWorkspace().currentWorkspace?.id ?? '';
  const state = parsePlayerReportsURLState(params, readableReportTypes);
  const type = state.type === 'all' ? undefined : state.type;
  const requestedType = params.get('type');
  const requestedPage = params.get('page');
  const parsedRequestedPage = requestedPage === null ? null : Number(requestedPage);
  const invalidRequestedPage = parsedRequestedPage !== null
    && (!Number.isSafeInteger(parsedRequestedPage) || parsedRequestedPage <= 0);
  const query = useQuery({
    queryKey: playerKeys.reports(workspaceId, playerId, type, state.page),
    queryFn: () => playersV2API.reports(playerId, {type, page: state.page, pageSize: 20}, workspaceId),
    enabled: Boolean(workspaceId && playerId),
  });

  useEffect(() => {
    const invalidRequestedType = Boolean(requestedType && state.type === 'all');
    if (!invalidRequestedType && !invalidRequestedPage) return;
    setParams(serializePlayerReportsURLState(params, {type: state.type, page: state.page}), {replace: true});
  }, [invalidRequestedPage, params, requestedType, setParams, state.page, state.type]);

  const updateState = (nextType: PlayerReportFilter, page: number) => {
    setParams(serializePlayerReportsURLState(params, {type: nextType, page}));
  };
  const filters: PlayerReportFilter[] = ['all', ...readableReportTypes];

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex min-w-0 flex-wrap gap-2" role="group" aria-label="Report type filters">
        {filters.map(filter => (
          <button
            key={filter}
            type="button"
            aria-pressed={state.type === filter}
            className={`min-h-11 rounded-full px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${state.type === filter ? 'bg-club-blue-700 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:ring-gray-700 dark:hover:bg-gray-800'}`}
            onClick={() => updateState(filter, 1)}
          >
            {filter === 'all' ? 'All' : labels[filter]}
          </button>
        ))}
      </div>

      {query.isPending && <DetailSection state={{status: 'loading', message: 'Loading reports...'}} />}
      {query.isError && <DetailSection state={{status: 'error', message: 'Unable to load reports.', retryLabel: 'Retry reports', retry: () => { void query.refetch(); }}} />}
      {query.data && !query.data.data.length && state.page === 1 && <DetailSection state={{status: 'empty', message: type ? `No ${type} reports yet.` : 'No reports yet.'}} />}
      {query.data && (query.data.data.length > 0 || state.page > 1) && (
        <div className="min-w-0 space-y-4">
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">{query.data.data.map((report: PlayerReport) => <ReportCard key={`${report.report_type}:${report.id}`} report={report} />)}</div>
          <ReportsPager page={state.page} hasNext={query.data.meta.has_more} onPage={page => updateState(state.type, page)} />
        </div>
      )}
    </div>
  );
};

export default PlayerReportsSection;
